
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import ResultsTable from "./ResultsTable";
import { getCurrentLevel } from "../level3/scoring/utils";
import ScoringPanel from "./ScoringPanel";
import { isChippingTest, isWedgingTest, isBunkerTest } from "../level3/scoring/utils";

interface ScoresState {
  [key: string]: (number | null)[];
}

interface LevelInfo {
  level: string;
}

interface TestInterfaceProps {
  selectedTest: string;
  currentPoint: string;
  currentAttempt: number;
  scores: ScoresState;
  onBackToTests: () => void;
  onFinish: () => void;
  onScoreChange: (score: number, tendency?: any) => void;
  onCancelLastEntry: () => void;
  getTestPoints: (testId: string) => string[];
  getTestDistances: (testId: string) => Record<string, string>;
  getTableHeadTitle: () => string;
  getFormattedPoint: (point: string) => string;
  getPointTotal: (point: string) => number;
  getGrandTotal: () => number;
  levels: Record<string, string | LevelInfo>;
}

const TestInterface: React.FC<TestInterfaceProps> = ({
  selectedTest,
  currentPoint,
  currentAttempt,
  scores,
  onBackToTests,
  onFinish,
  onScoreChange,
  onCancelLastEntry,
  getTestPoints,
  getTestDistances,
  getTableHeadTitle,
  getFormattedPoint,
  getPointTotal,
  getGrandTotal,
  levels
}) => {
  const { t, i18n } = useTranslation("training");
  const currentLevel = 2;
  const maxAttempts = 10;
  const testPoints = getTestPoints(selectedTest);
  const distance = getTestDistances(selectedTest)[currentPoint] || "";
  
  const isWedging = isWedgingTest(selectedTest);
  const isChipping = isChippingTest(selectedTest);
  const isBunker = isBunkerTest(selectedTest);

  // Map test IDs to their categories for translation
  const testCategoryMap: Record<string, string> = {
    'putts-plat': 'putting',
    'putts-montee': 'putting',
    'putts-descente': 'putting',
    'putts-pente': 'putting',
    'putts-pente-droite': 'putting',
    'chipping-plat': 'chipping',
    'chipping-pente-droit': 'chipping',
    'chipping-pente-gauche': 'chipping',
    'chipping-roulee-plat': 'chipping',
    'wedging-cercles': 'wedging',
    'wedging-mid': 'wedging',
    'wedging-pente-gauche': 'wedging',
    'bunker-court': 'bunker',
    'bunker-long': 'bunker',
    'iron-long': 'iron',
    'iron-mid': 'iron',
    'iron-green-attack-100': 'iron',
    'fairwayWood-3': 'fairwayWood',
    'fairwayWood-5': 'fairwayWood',
    'driver-practice': 'driver',
    'driver-accuracy': 'driver'
  };

  const category = testCategoryMap[selectedTest];
  const titleKey = `level2.testData.${category}.${selectedTest}.title`;
  const descriptionKey = `level2.testData.${category}.${selectedTest}.description`;
  
  const testTitle = t(titleKey);
  const testDescription = t(descriptionKey);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-12"
    >
      <Card className="w-full mx-auto mt-2 md:mt-6">
        <CardHeader className="pb-2 md:pb-6">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              className="mb-2"
              onClick={onBackToTests}
              type="button"
            >
              <ChevronLeft className="mr-1" /> {t("level2.interface.backToTests")}
            </Button>
          </div>
          <CardTitle className="text-xl md:text-2xl text-golf-700">{testTitle}</CardTitle>
          <CardDescription className="text-base mt-2">
            {testDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 md:space-y-6">
            <ScoringPanel 
              testId={selectedTest}
              currentPoint={currentPoint}
              currentAttempt={currentAttempt}
              maxAttempts={maxAttempts}
              distance={distance}
              onScoreChange={onScoreChange}
              onCancelLastEntry={onCancelLastEntry}
              isChippingTest={isChipping}
              isWedgingTest={isWedging}
              isBunkerTest={isBunker}
            />

            <ResultsTable 
              selectedTest={selectedTest}
              scores={scores}
              testPoints={testPoints}
              getTestDistances={getTestDistances}
              getTableHeadTitle={getTableHeadTitle}
              getFormattedPoint={getFormattedPoint}
              getPointTotal={getPointTotal}
              getGrandTotal={getGrandTotal}
              levels={levels}
            />
            
            <div className="flex justify-end mt-4">
              <Button onClick={onFinish} className="bg-golf-600 hover:bg-golf-700">
                {t("level2.interface.finish")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TestInterface;
